import React, { Component, Fragment } from 'react';
import injectSheet from 'react-jss';
import { Link } from 'react-router-dom-v5-compat';

import { PAGE_SIZE, PAGE_START } from '~/common/constants/page';
import {
  createDocumentGroup,
  deleteDocumentGroup,
  editDocumentGroup,
  getAllDocumentGroups,
} from '~/components/DocumentGroupsPage/DocumentGroupsPage.data';
import styles from '~/components/DocumentGroupsPage/DocumentGroupsPage.styles';
import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import EcModal from '~/components/Shared/EcModal/EcModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import PageTitle from '~/components/Shared/PageTitle/PageTitle';
import ReactTable from '~/components/Shared/ReactTable/ReactTable';
import { Button } from '~/eds/src/components/Button/Button';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { QueryParamType } from '~/enums/QueryParamType';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withPermissions } from '~/hocs/withPermissions';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { MODAL_DELETE, MODAL_NAME } from '~/types/modal.types';
import { ERROR, SUCCESS, WARNING } from '~/types/toast.types';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';

const PERMISSIONS = [
  {
    resourceId: 'document_groups',
    resourceType: 'edit',
  },
];
class Page extends Component {
  constructor(props) {
    super(props);

    this.onGroupCreate = this.onGroupCreate.bind(this);
    this.onGroupEdit = this.onGroupEdit.bind(this);
    this.onGroupDelete = this.onGroupDelete.bind(this);

    this.state = {
      loadingData: false,
      groupData: [],
      selectedItem: null,
      currentModal: null,
    };

    this.urlParams = {
      page: getPageSearchQueryByKey(QueryParamType.Page, PAGE_START),
      pageSize: getPageSearchQueryByKey(QueryParamType.PageSize, PAGE_SIZE),
      sortedColumn: getPageSearchQueryByKey(
        QueryParamType.SortedColumn,
        'lastModified',
      ),
      sortedOrder: getPageSearchQueryByKey(QueryParamType.SortedOrder, 'true'),
    };
  }

  componentDidMount() {
    this.setState({ ...this.urlParams }, () => {
      this.loadDocumentGroupsData();
      updatePageSearchQuery(this.urlParams);
    });
  }

  loadDocumentGroupsData() {
    this.setState({ loadingData: true });
    getAllDocumentGroups()
      .then((groupData) => {
        this.setState({
          groupData,
          loadingData: false,
          errorLoadingMessage: false,
        });
      })
      .catch(() => {
        this.setState({ loadingData: false, errorLoadingMessage: true });
      });
  }

  handleShowModal = (currentModal, selectedItem = null) => {
    this.setState({ currentModal, selectedItem });
  };

  handleHideModal = () => {
    this.setState({ currentModal: null, selectedItem: null });
  };

  onGroupCreate(name, note) {
    const { intl } = this.props;
    createDocumentGroup({ name, note })
      .then((res) => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'features.DocumentGroups.groupCreated',
              defaultMessage: '{groupName} has been created.',
              description:
                'Success message when document group is created. Example: groupName = "Contract Group"',
            },
            { groupName: res.name },
          ),
        );
        this.setState(
          {
            page: PAGE_START,
            sortedColumn: 'lastModified',
            sortedOrder: 'true',
          },
          () => {
            this.loadDocumentGroupsData();
            updatePageSearchQuery({
              [QueryParamType.Page]: PAGE_START,
              [QueryParamType.SortedColumn]: 'lastModified',
              [QueryParamType.SortedOrder]: 'true',
            });
          },
        );
        this.handleHideModal();
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          showToast(ERROR, `${err.response?.data?.message}`);
        } else {
          showToast(
            WARNING,
            intl.formatMessage({
              id: 'features.DocumentGroups.errorCreatingGroup',
              defaultMessage:
                'An error occurred while creating a new document group.',
              description: 'Error message when document group creation fails',
            }),
          );
        }
      });
  }

  onGroupEdit(name, note) {
    const { selectedItem } = this.state;
    const { intl } = this.props;
    editDocumentGroup(selectedItem.groupId, { name, note })
      .then((res) => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'features.DocumentGroups.groupUpdated',
              defaultMessage: '{groupName} has been updated.',
              description:
                'Success message when document group is updated. Example: groupName = "Contract Group"',
            },
            { groupName: res.name },
          ),
        );
        this.loadDocumentGroupsData();
        this.handleHideModal();
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          showToast(ERROR, `${err.response?.data?.message}`);
        } else {
          showToast(
            WARNING,
            intl.formatMessage({
              id: 'features.DocumentGroups.errorUpdatingGroup',
              defaultMessage:
                'An error occurred while updating a new document group.',
              description: 'Error message when document group update fails',
            }),
          );
        }
      });
  }

  onGroupDelete() {
    const { selectedItem } = this.state;
    const { intl } = this.props;
    deleteDocumentGroup(selectedItem.groupId)
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'features.DocumentGroups.groupDeleted',
            defaultMessage: 'The group has been deleted.',
            description: 'Success message when document group is deleted',
          }),
        );
        this.loadDocumentGroupsData();
        this.handleHideModal();
      })
      .catch(() => {
        showToast(
          WARNING,
          intl.formatMessage({
            id: 'features.DocumentGroups.errorDeletingGroup',
            defaultMessage:
              'An error occurred while deleting the document group.',
            description: 'Error message when document group deletion fails',
          }),
        );
      });
  }

  hasEditPermission = () => {
    const { checkPermission } = this.props;

    return checkPermission('document_groups', 'edit');
  };

  renderNameCell = (item) => {
    const { classes } = this.props;

    return (
      <Link className={classes.nameCell} to={`/document-group/${item.groupId}`}>
        {item.name}
      </Link>
    );
  };

  renderActionMenuCell = (item) => {
    const { intl } = this.props;
    const items = [
      {
        content: intl.formatMessage({
          id: 'features.DocumentGroups.edit',
          defaultMessage: 'Edit',
          description: 'Menu item label for edit action',
        }),
        onClick: () => this.handleShowModal(MODAL_NAME, item),
      },
      {
        content: intl.formatMessage({
          id: 'features.DocumentGroups.delete2',
          defaultMessage: 'Delete',
          description: 'Delete text',
        }),
        onClick: () => this.handleShowModal(MODAL_DELETE, item),
      },
    ];

    return (
      <ActionsMenu
        id={`documents_row_actions_menu?groupId=${item.groupId}`}
        disabled={!this.hasEditPermission()}
        items={items}
        smallIcon
        title={intl.formatMessage({
          id: 'features.DocumentGroups.editGroupMenuTitle',
          defaultMessage: 'edit group documents menu',
          description: 'Accessibility title for group action menu',
        })}
      />
    );
  };

  renderDateAddedCell = (item) => {
    return this.props.formatDate(item.dateAdded, 'long');
  };

  renderLastUpdatedDateCell = (item) => {
    return this.props.formatDate(item.lastModified, 'long');
  };

  renderNumberOfDocumentsCell = (row) => {
    const { intl } = this.props;
    return intl.formatMessage(
      {
        id: 'features.DocumentGroups.documentCount',
        defaultMessage:
          '{count} {count, plural, one {document} other {documents}}',
        description:
          'Display count of documents with pluralization. Example: count = "3"',
      },
      { count: row.numberOfDocs },
    );
  };

  renderDocumentGroupsTable() {
    const { groupData, page, pageSize, sortedColumn, sortedOrder } = this.state;
    const { intl } = this.props;
    let columns = [
      {
        label: '',
        value: 'actionMenu',
        width: 56,
        sortable: false,
        renderCustomCell: this.renderActionMenuCell,
      },
      {
        label: intl.formatMessage({
          id: 'features.DocumentGroups.name',
          defaultMessage: 'Name',
          description: 'Column header for group name',
        }),
        value: 'name',
        width: 400,
        resizable: true,
        renderCustomCell: this.renderNameCell,
      },
      {
        label: intl.formatMessage({
          id: 'features.DocumentGroups.createdBy',
          defaultMessage: 'Created By',
          description: 'Column header for creator',
        }),
        value: 'createdBy',
      },
      {
        label: intl.formatMessage({
          id: 'features.DocumentGroups.createdDate',
          defaultMessage: 'Created Date',
          description: 'Column header for creation date',
        }),
        value: 'dateAdded',
        disableGlobalFilter: true,
        renderCustomCell: this.renderDateAddedCell,
      },
      {
        label: intl.formatMessage({
          id: 'features.DocumentGroups.lastUpdated',
          defaultMessage: 'Last Updated',
          description: 'Column header for last updated date',
        }),
        value: 'lastModified',
        disableGlobalFilter: true,
        renderCustomCell: this.renderLastUpdatedDateCell,
      },
      {
        label: intl.formatMessage({
          id: 'features.DocumentGroups.numberOfDocuments',
          defaultMessage: '# of Documents',
          description: 'Column header for document count',
        }),
        value: 'numberOfDocs',
        disableGlobalFilter: true,
        renderCustomCell: this.renderNumberOfDocumentsCell,
      },
      {
        label: intl.formatMessage({
          id: 'features.DocumentGroups.notes',
          defaultMessage: 'Notes',
          description: 'Column header for notes',
        }),
        value: 'note',
        width: 400,
      },
    ];

    const disableSorting =
      this.props.flags[FlagType.DisableDocumentTableSorting] ||
      this.props.flags[FlagType.FolderContentPrefetch];
    if (disableSorting) {
      columns = columns.map((column) => ({
        ...column,
        sortable: false,
      }));
    }

    const tableSettings = {
      page,
      pageSize,
      sortedColumn: disableSorting ? '' : sortedColumn,
      sortedOrder: disableSorting ? 'false' : sortedOrder,
    };

    return (
      <ReactTable
        columns={columns}
        data={groupData.results}
        title={intl.formatMessage({
          id: 'features.DocumentGroups.tableTitle',
          defaultMessage: 'document groups',
          description: 'Table title for document groups',
        })}
        handleTablePropsChange={updatePageSearchQuery}
        settings={tableSettings}
      />
    );
  }

  renderEmptyDocumentGroups() {
    const { intl } = this.props;
    return (
      <EmptyPage
        preset="no-groups"
        resolveAction={{
          icon: 'plus',
          id: 'documents_groups_empty_newGroupButton',
          onClick: () => this.handleShowModal(MODAL_NAME),
          text: intl.formatMessage({
            id: 'features.DocumentGroups.newGroup2',
            defaultMessage: 'New Group',
            description: 'Text for creating new group',
          }),
          disabled: !this.hasEditPermission(),
        }}
      />
    );
  }

  renderNameModal() {
    const { selectedItem } = this.state;
    const { intl } = this.props;

    let title;
    const labelText = intl.formatMessage({
      id: 'features.DocumentGroups.groupName',
      defaultMessage: 'GROUP NAME',
      description: 'Label for group name field',
    });
    let confirmButtonIcon;
    let confirmButtonText;
    let currentName;
    const labelTextArea = intl.formatMessage({
      id: 'features.DocumentGroups.note',
      defaultMessage: 'NOTE',
      description: 'Label for note field',
    });
    let itemsCurrentDescription;
    const includeTextArea = true;
    let handleNameChange;

    if (selectedItem) {
      title = intl.formatMessage({
        id: 'features.DocumentGroups.editGroup',
        defaultMessage: 'Edit Group',
        description: 'Modal title for editing group',
      });
      currentName = selectedItem.name;
      itemsCurrentDescription = selectedItem.note;
      confirmButtonText = intl.formatMessage({
        id: 'features.DocumentGroups.save',
        defaultMessage: 'Save',
        description: 'Save button text',
      });
      handleNameChange = this.onGroupEdit;
    } else {
      title = intl.formatMessage({
        id: 'features.DocumentGroups.newGroup',
        defaultMessage: 'New Group',
        description: 'Text for creating new group',
      });
      currentName = '';
      itemsCurrentDescription = '';
      confirmButtonIcon = <Icon icon="plus" />;
      confirmButtonText = intl.formatMessage({
        id: 'features.DocumentGroups.createGroup',
        defaultMessage: 'Create Group',
        description: 'Create group button text',
      });
      handleNameChange = this.onGroupCreate;
    }

    return (
      <EcModal
        modalType={MODAL_NAME}
        width="560px"
        title={title}
        labelText={labelText}
        itemsCurrentName={currentName}
        includeTextArea={includeTextArea}
        labelTextArea={labelTextArea}
        itemsCurrentDescription={itemsCurrentDescription}
        confirmButtonIcon={confirmButtonIcon}
        confirmButtonText={confirmButtonText}
        handleNameChange={handleNameChange}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderDeleteGroupModalText() {
    const { selectedItem } = this.state;

    return (
      <Fragment>
        <FormattedMessage
          id="features.DocumentGroups.deleteConfirmation"
          defaultMessage="Are you sure you want to delete the group <strong>{groupName}</strong>? You won't be able to undo this action."
          description="Confirmation message for deleting a group. Example: groupName = 'Contract Group'"
          values={{
            groupName: selectedItem.name,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </Fragment>
    );
  }

  renderDeleteModal() {
    const { intl } = this.props;
    return (
      <Modal
        children={<Text>{this.renderDeleteGroupModalText()}</Text>}
        isVisible
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        primaryAction={{
          text: intl.formatMessage({
            id: 'features.DocumentGroups.delete',
            defaultMessage: 'Delete',
            description: 'Delete text',
          }),
          onClick: () => {
            this.onGroupDelete();
            this.handleHideModal();
          },
          variant: 'danger',
        }}
        title={intl.formatMessage({
          id: 'features.DocumentGroups.deleteGroupTitle',
          defaultMessage: 'Delete Group?',
          description: 'Modal title for deleting group',
        })}
      />
    );
  }

  render() {
    const { classes, intl } = this.props;
    const { groupData, currentModal, loadingData, errorLoadingMessage } =
      this.state;

    const { isPermissionsLoading } = this.props;

    const loading = loadingData || isPermissionsLoading;

    return (
      <div>
        <div className={classes.documentHeader}>
          <div className={classes.documentFullTitle}>
            <PageTitle
              title={intl.formatMessage({
                id: 'features.DocumentGroups.pageTitle',
                defaultMessage: 'Document Groups',
                description: 'Page title for document groups',
              })}
            />
          </div>
          <div className={classes.documentHeaderActions}>
            {!loading && groupData.results && groupData.results.length ? (
              <Button
                id="documents_header_newGroupButton"
                onClick={() => {
                  this.handleShowModal(MODAL_NAME);
                }}
                text={intl.formatMessage({
                  id: 'features.DocumentGroups.newGroup',
                  defaultMessage: 'New Group',
                  description: 'Text for creating new group',
                })}
                icon="plus"
                iconPosition="left"
                variant="primary"
                disabled={!this.hasEditPermission()}
              ></Button>
            ) : null}
          </div>
        </div>
        {loading && !errorLoadingMessage ? (
          <div className={classes.loadingContainer}>
            <LoadingSpinner size="medium" />
          </div>
        ) : !loading && errorLoadingMessage ? (
          <div>
            <FormattedMessage
              id="features.DocumentGroups.errorLoading"
              defaultMessage="An error occurred when loading the document groups."
              description="Error message when document groups fail to load"
            />
          </div>
        ) : !loading &&
          !errorLoadingMessage &&
          groupData &&
          groupData.results &&
          groupData.results.length ? (
          this.renderDocumentGroupsTable()
        ) : (
          this.renderEmptyDocumentGroups()
        )}
        {currentModal === MODAL_NAME && this.renderNameModal()}
        {currentModal === MODAL_DELETE && this.renderDeleteModal()}
      </div>
    );
  }
}

export default withPermissions(
  injectSheet(styles)(withFlags(injectIntl(withFormatDate(Page)))),
  PERMISSIONS,
);
